/**
 * The curved-wall room, in metres, sized from the film it is given. The wall is an arc of a
 * cylinder standing on the origin; a viewer at the origin stands at its centre of curvature.
 *
 * A wide panorama wraps far around the viewer at a fixed height (Visions, 1920 x 430, wraps
 * about 118 degrees; Memememory, 3936 x 720, about 144). A narrower film would be a sliver at
 * that height, so it gets a taller wall instead, down to a 16:9 film on a screen ten metres
 * wide. Past either end the wall stops growing and the other measure gives way.
 */
export interface WallGeometry {
  radius: number;
  height: number;
  /** Gap between the floor and the wall's lower edge. */
  bottom: number;
  /** The angle the wall wraps around the origin, in radians. */
  theta: number;
  /** Half that angle in degrees: the wall's two ends as seen from the origin. */
  half: number;
  centreY: number;
  segments: number;
}

const RADIUS = 9.6;
const BOTTOM = 0.3;
/** Wrap, in radians, per unit of aspect while the height holds at 4.4 m. */
const WRAP_PER_ASPECT = 0.458;
const MIN_THETA = 1.05;
const MAX_THETA = 2.62;
const MAX_HEIGHT = 6.2;
/** A wall bent on request never closes round the viewer: its two ends stop short of facing each other. */
const MAX_BENT_THETA = 3.3;

export const EYE_HEIGHT = 1.65;

/**
 * `bend` above 1 asks for a tighter curve: the wall keeps its size, and wraps further round a
 * radius that much shorter (1.35 turns a 119 degree arc into one of 161).
 */
export function wallGeometry(aspect: number, bend = 1): WallGeometry {
  const a = Math.min(12, Math.max(0.8, aspect || 16 / 9));
  let theta = Math.min(MAX_THETA, Math.max(MIN_THETA, a * WRAP_PER_ASPECT));
  let height = (RADIUS * theta) / a;
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    theta = (height * a) / RADIUS;
  }
  const tighter = Math.max(1, Math.min(bend || 1, MAX_BENT_THETA / theta));
  theta *= tighter;
  return {
    radius: RADIUS / tighter,
    height,
    bottom: BOTTOM,
    theta,
    half: (theta / 2) * (180 / Math.PI),
    centreY: BOTTOM + height / 2,
    segments: 192,
  };
}

/**
 * Where a visitor may walk: the disc inside the wall (so the camera never passes through it)
 * joined to a strip of the same width behind it.
 */
export function clampToWall(g: WallGeometry) {
  const inner = g.radius - 1.1;
  const maxZ = 15;
  return (p: { x: number; z: number }) => {
    if (p.z < 0) {
      const d = Math.hypot(p.x, p.z);
      if (d > inner) {
        const s = inner / d;
        p.x *= s;
        p.z *= s;
      }
    } else {
      if (p.x > inner) p.x = inner;
      if (p.x < -inner) p.x = -inner;
      if (p.z > maxZ) p.z = maxZ;
    }
  };
}
